import React from 'react';
import { Route, IndexRoute, Redirect, IndexRedirect } from 'react-router';
import { instantScrollTop } from 'swag-client-common/utils/tricks';
import { APP_ROUTES } from 'swag-client-common/constants/app-routes';
import { CollectSizesUrl } from 'swag-common/utils/urls/collect-sizes.url';
import { Home } from '../components/routes/client/home';
import { ProfilePassword } from '../components/routes/account/profile-password';
import { ProfileContainer } from '../containers/profile/profile-container';
import { PublicApi } from '../components/routes/account/public-api';
import { AccountApiSettings } from '../components/routes/account/public-api-settings';
import { AccountApiPayment } from '../components/routes/account/public-api-payment';
import { applyUserPlan, onCategoriesEnter, onSpringEnter, onKickstarterCalculatorEnter, onCompanyDepartments, onPendingOrders, onPastWorkEnter, onProfilePasswordLeave, requiredToBeLoggedIn, onInaboxLandingEnter, onDistributeLandingEnter, onApiSettingsEnter, onApiPaymentSettingsEnter, onSizeSubmissions, onSizeSubmissionsLeave, onSizeSelectionEnter, onSizeSelectionSuccessEnter, onAccountMockups, onInvitationEnter } from './route-hooks/route-hooks';
const LazyPrivacyPolicy = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[PrivacyPolicy]" */'../components/routes/client/privacy-policy');
});
const LazyCompanyDepartments = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[CompanyDepartments]" */'../components/routes/account/company-departments');
});
const LazyOffice = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[Office]" */'../components/routes/client/office');
});
const LazyStarbucks = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[Starbucks]" */'../components/routes/client/starbucks');
});
const LazyKSCalculator = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[KSCalculator]" */'../containers/ks-calculator');
});
const LazyCustomSwag = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[CustomSwag]" */'../containers/custom-swag');
});
const LazyOurStory = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[OurStory]" */'../components/routes/client/our-story');
});
const LazyTOS = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[TOS]" */'../components/routes/client/tos');
});
const LazyCommitmentToAccessibility = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[CommitmentToAccessibility]" */'../components/routes/client/commitment-to-accessibility').then(module => ({
    default: module.CommitmentToAccessibility
  }));
});
const LazyContact = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[Contact]" */'../containers/contact');
});
const LazyFaq = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[Faq]" */'../components/routes/client/faq');
});
const LazyTechstars = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[Techstars]" */'../components/routes/client/techstars');
});
const LazySpring = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[Spring]" */'../components/routes/client/spring');
});
const LazyWework = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[Wework]" */'../components/routes/client/wework');
});
const LazyAmericanUnderground = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[AmericanUnderground]" */'../components/routes/client/american-underground');
});
const LazyPrimoprint = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[Primoprint]" */'../components/routes/client/primoprint');
});
const LazyIndustrious = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[Industrious]" */'../components/routes/client/industrious');
});
const LazyMyCompany = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[MyCompany]" */'../components/routes/account/my-company');
});
const LazyPendingOrders = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[PendingOrders]" */'../components/routes/account/pending-orders');
});
const LazyLandingInventory = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[LandingInventory]" */'../components/routes/client/landing-inventory').then(({
    LandingInventory
  }) => ({
    default: LandingInventory
  }));
});
const LazyProductListing = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[ProductListing]" */'../components/routes/client/product-listing');
});
const LazyAwYouGuys = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[LandingAwYouGuys]" */'../components/routes/client/aw-you-guys');
});
const LazyPastWork = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[LandingPastWork]" */'../components/routes/client/past-work');
});
const LazyLandingInabox = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[LandingInabox]" */'../components/routes/client/landing-inabox').then(module => ({
    default: module.LandingInabox
  }));
});
const LazySizeSubmissions = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[SizeSubmissions]" */'../components/routes/account/size-submissions').then(module => ({
    default: module.SizeSubmissions
  }));
});
const LazySizeSelection = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[SizeSelection]" */'../components/routes/client/landing-size-selection').then(module => ({
    default: module.LandingSizeSelection
  }));
});
const LazySizeSelectionSuccess = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[SizeSelectionSuccess]" */'../components/routes/client/landing-size-selection-success').then(module => ({
    default: module.LandingSizeSelectionSuccess
  }));
});
const LazyAccountMockups = /*#__PURE__*/React.lazy(() => {
  return import( /* webpackChunkName: "[AccountMockups]" */'../components/routes/account/mockups');
});
export const OtherRoutes = ({
  store,
  dispatch
}) => {
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(IndexRoute, {
    component: Home,
    onEnter: () => {
      instantScrollTop();
    }
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.MY_COMPANY,
    component: props => /*#__PURE__*/React.createElement(LazyMyCompany, props)
  }, /*#__PURE__*/React.createElement(IndexRedirect, {
    to: "pending-orders"
  }), /*#__PURE__*/React.createElement(Route, {
    path: "departments",
    component: props => /*#__PURE__*/React.createElement(LazyCompanyDepartments, props),
    onEnter: onCompanyDepartments(store, dispatch)
  }), /*#__PURE__*/React.createElement(Route, {
    path: "pending-orders",
    component: props => /*#__PURE__*/React.createElement(LazyPendingOrders, props),
    onEnter: onPendingOrders(store, dispatch)
  })), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.ACCOUNT_PROFILE,
    component: ProfileContainer,
    onEnter: requiredToBeLoggedIn(store)
  }, /*#__PURE__*/React.createElement(Route, {
    path: "password",
    component: ProfilePassword,
    onLeave: onProfilePasswordLeave(store, dispatch)
  })), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.CUSTOM_SWAG,
    component: props => /*#__PURE__*/React.createElement(LazyCustomSwag, props),
    onEnter: instantScrollTop
  }), /*#__PURE__*/React.createElement(Route, {
    path: `${APP_ROUTES.WEWORK}/login/:token`,
    onEnter: applyUserPlan(store, dispatch)
  }), ",", /*#__PURE__*/React.createElement(Redirect, {
    from: APP_ROUTES.KICKSTARTER,
    to: `${APP_ROUTES.KICKSTARTER}/all-swag`
  }), ",", /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.STARBUCKS,
    component: props => /*#__PURE__*/React.createElement(LazyStarbucks, props),
    onEnter: instantScrollTop
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.OFFICE,
    component: props => /*#__PURE__*/React.createElement(LazyOffice, props),
    onEnter: instantScrollTop
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.TECHSTARS,
    component: props => /*#__PURE__*/React.createElement(LazyTechstars, props)
  }), ",", /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.SPRING,
    component: props => /*#__PURE__*/React.createElement(LazySpring, props),
    onEnter: onSpringEnter
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.WEWORK,
    component: props => /*#__PURE__*/React.createElement(LazyWework, props)
  }), ",", /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.AMERICANUNDERGROUND,
    component: props => /*#__PURE__*/React.createElement(LazyAmericanUnderground, props)
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.INDUSTRIOUS,
    component: props => /*#__PURE__*/React.createElement(LazyIndustrious, props)
  }), ",", /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.PRIMOPRINT,
    component: props => /*#__PURE__*/React.createElement(LazyPrimoprint, props)
  }), ",", /*#__PURE__*/React.createElement(Route, {
    path: `${APP_ROUTES.KICKSTARTER}/:categorySlug(/:subcategorySlug)`,
    onEnter: onCategoriesEnter(store, dispatch),
    component: props => /*#__PURE__*/React.createElement(LazyProductListing, props)
  }), /*#__PURE__*/React.createElement(Route, {
    path: `${APP_ROUTES.TECHSTARS_PRODUCTS}/:categorySlug(/:subcategorySlug)`,
    onEnter: onCategoriesEnter(store, dispatch),
    component: props => /*#__PURE__*/React.createElement(LazyProductListing, props)
  }), /*#__PURE__*/React.createElement(Route, {
    path: `${APP_ROUTES.SPRING_PRODUCTS}/:categorySlug(/:subcategorySlug)`,
    onEnter: onCategoriesEnter(store, dispatch),
    component: props => /*#__PURE__*/React.createElement(LazyProductListing, props)
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.KICKSTARTER_CALCULATOR,
    component: props => /*#__PURE__*/React.createElement(LazyKSCalculator, props),
    onEnter: onKickstarterCalculatorEnter()
  }), /*#__PURE__*/React.createElement(Redirect, {
    from: "pages/our-story",
    to: APP_ROUTES.OUR_STORY
  }), /*#__PURE__*/React.createElement(Redirect, {
    from: "pages/contact",
    to: APP_ROUTES.CONTACT
  }), /*#__PURE__*/React.createElement(Redirect, {
    from: "pages/faq",
    to: APP_ROUTES.FAQ
  }), /*#__PURE__*/React.createElement(Redirect, {
    from: "pages/custom-swag",
    to: APP_ROUTES.CUSTOM_SWAG
  }), /*#__PURE__*/React.createElement(Redirect, {
    from: "pages/swag-swap",
    to: APP_ROUTES.HOME
  }), /*#__PURE__*/React.createElement(Redirect, {
    from: "/swag-swap",
    to: APP_ROUTES.HOME
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.OUR_STORY,
    component: props => /*#__PURE__*/React.createElement(LazyOurStory, props),
    onEnter: instantScrollTop
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.TOS,
    component: props => /*#__PURE__*/React.createElement(LazyTOS, props),
    onEnter: instantScrollTop
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.COMMITMENT_TO_ACCESSIBILITY,
    component: props => /*#__PURE__*/React.createElement(LazyCommitmentToAccessibility, props),
    onEnter: instantScrollTop
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.CONTACT,
    component: props => /*#__PURE__*/React.createElement(LazyContact, props),
    onEnter: instantScrollTop
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.FAQ,
    component: props => /*#__PURE__*/React.createElement(LazyFaq, props),
    onEnter: instantScrollTop
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.PRIVACY_POLICY,
    component: props => /*#__PURE__*/React.createElement(LazyPrivacyPolicy, props),
    onEnter: instantScrollTop
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.DISTRIBUTE,
    component: props => /*#__PURE__*/React.createElement(LazyLandingInventory, props),
    onEnter: onDistributeLandingEnter(store, dispatch)
  }), /*#__PURE__*/React.createElement(Redirect, {
    from: "inventory",
    to: APP_ROUTES.DISTRIBUTE
  }), /*#__PURE__*/React.createElement(Redirect, {
    from: "/masks-for-all",
    to: APP_ROUTES.HOME
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.REVIEWS,
    component: props => /*#__PURE__*/React.createElement(LazyAwYouGuys, props),
    onEnter: instantScrollTop
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.PAST_WORK,
    component: props => /*#__PURE__*/React.createElement(LazyPastWork, props),
    onEnter: onPastWorkEnter(store, dispatch)
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.IN_A_BOX,
    component: props => /*#__PURE__*/React.createElement(LazyLandingInabox, props),
    onEnter: onInaboxLandingEnter(store, dispatch)
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.SIZE_SUBMISSIONS,
    component: props => /*#__PURE__*/React.createElement(LazySizeSubmissions, props),
    onEnter: onSizeSubmissions(store, dispatch),
    onLeave: onSizeSubmissionsLeave(store, dispatch)
  }), /*#__PURE__*/React.createElement(Route, {
    path: CollectSizesUrl.getCollectSizesSubmitLanding({
      link: ':link'
    }),
    component: props => /*#__PURE__*/React.createElement(LazySizeSelection, props),
    onEnter: onSizeSelectionEnter(store, dispatch)
  }), /*#__PURE__*/React.createElement(Route, {
    path: CollectSizesUrl.getCollectSizesSubmitSuccess({
      link: ':link'
    }),
    component: props => /*#__PURE__*/React.createElement(LazySizeSelectionSuccess, props),
    onEnter: onSizeSelectionSuccessEnter(store, dispatch)
  }), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.ACCOUNT_PUBLIC_API,
    component: PublicApi,
    onEnter: onApiSettingsEnter(store, dispatch)
  }, /*#__PURE__*/React.createElement(IndexRedirect, {
    to: "settings"
  }), /*#__PURE__*/React.createElement(Route, {
    path: "settings",
    component: AccountApiSettings
  }), /*#__PURE__*/React.createElement(Route, {
    path: "payment",
    component: AccountApiPayment,
    onEnter: onApiPaymentSettingsEnter(store, dispatch)
  })), /*#__PURE__*/React.createElement(Route, {
    path: APP_ROUTES.ACCOUNT_SAVED_DESIGNS,
    component: props => /*#__PURE__*/React.createElement(LazyAccountMockups, props),
    onEnter: onAccountMockups(store, dispatch)
  }), /*#__PURE__*/React.createElement(Route, {
    path: `${APP_ROUTES.INVITATION}/:inviteId`,
    component: Home,
    onEnter: onInvitationEnter(store, dispatch)
  }));
};